import { money } from "@stb/core/money"
import { calculateDocumentGst, calculateLineGst } from "@stb/jurisdiction-au"

import { GstCalculator, SimpleTable, SourceLine, formatAud } from "../components/landing"
import type { Guide } from "./guide-types"

/**
 * Served at /gst-calculator (T915), not under /guides. Spec: docs/specs/pay-calculator-spec.md section 8; PRD FT2.
 * Copy T1040: every GST fact below comes from the four ATO pages linked here, read on `checked`.
 * Kestly is not registered for GST: never write GST wording about Kestly's own prices.
 */
const ATO_GST =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst"
const ATO_HOW_GST_WORKS =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/how-gst-works"
const ATO_REGISTER =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/registering-for-gst"
const ATO_TAX_INVOICES =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices"
const checked = "30 Oct 2026"
const fy = "2026–27"

/** The default example (A$110 including GST), from the same engine the calculator uses. */
const example = calculateLineGst({
  amount: money(11000, "AUD"),
  taxCode: "GST",
  amountsAre: "inclusive",
})

/** Adding GST to a price that has none yet (A$250). */
const addExample = calculateLineGst({
  amount: money(25000, "AUD"),
  taxCode: "GST",
  amountsAre: "exclusive",
})

/** Taking GST out of a total that is not a round number (A$99.95). */
const removeExample = calculateLineGst({
  amount: money(9995, "AUD"),
  taxCode: "GST",
  amountsAre: "inclusive",
})

/** Several-items example (GST included, rounded per line), from the same engine the calculator uses. */
const EXAMPLE_ITEMS: [string, number][] = [
  ["Brake pads", 11000],
  ["Oil and filter", 5555],
  ["Wiper blades", 1999],
]
const itemsExample = calculateDocumentGst(
  EXAMPLE_ITEMS.map(([, cents]) => ({ amount: money(cents, "AUD"), taxCode: "GST" as const })),
  { amountsAre: "inclusive", rounding: "per-line" },
)

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const h2 = "text-2xl font-semibold"
const para = "max-w-[70ch]"
const list = "list-disc space-y-1 pl-6"

export const gstGuide: Guide = {
  slug: "gst-calculator",
  title: "GST calculator Australia: add or remove 10% GST",
  description:
    "Free GST calculator for Australia. Add 10% GST to a price or remove it from a total (divide by 11), with rounding, the A$75,000 threshold and ATO sources.",
  crumb: "GST calculator",
  h1: "GST calculator Australia: add or remove GST",
  published: "2026-10-30",
  reviewed: "2026-10-30",
  badge: "Free tool",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO, how GST works", href: ATO_HOW_GST_WORKS },
    { label: "registering for GST", href: ATO_REGISTER },
    { label: "tax invoices", href: ATO_TAX_INVOICES },
  ],
  answer: (
    <>
      GST in Australia is 10% of the price before GST. To add GST, multiply that price by 1.1. To
      find the GST in a price that already includes it, divide the price by 11, then subtract the
      result to get the price without GST. For example, {formatAud(example.gross)} including GST is{" "}
      {formatAud(example.net)} plus {formatAud(example.gst)} GST.
    </>
  ),
  related: [
    { label: "Invoicing in Kestly: send tax invoices and see who has paid", href: "/invoicing" },
    { label: "Free invoice generator: make a PDF tax invoice", href: "/invoice-generator" },
    {
      label: "Tax invoice requirements: what each invoice must show",
      href: "/guides/tax-invoice-requirements",
    },
    { label: "Pay calculator: take-home pay after tax", href: "/pay-calculator" },
    {
      label: "Cents per km calculator: this year's ATO car rate",
      href: "/cents-per-km-calculator",
    },
    { label: "All guides and free tools", href: "/guides" },
  ],
  cta: {
    title: "Kestly works out GST on your tax invoices",
    description: "Make a tax invoice that adds the GST for you. Free, no account needed.",
    ctaLabel: "Open the invoice generator",
    ctaHref: "/invoice-generator?from=gst-calculator",
  },
  toc: [
    { id: "calculator", label: "Calculate GST" },
    { id: "formulas", label: "GST formulas" },
    { id: "add-gst", label: "Add GST" },
    { id: "remove-gst", label: "Remove GST" },
    { id: "inclusive-exclusive", label: "Inclusive vs exclusive" },
    { id: "several-items", label: "Rounding" },
    { id: "what-is-gst", label: "What GST is" },
    { id: "threshold", label: "Registration threshold" },
    { id: "tax-invoices", label: "Tax invoices" },
    { id: "faq", label: "Common questions" },
  ],
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className={h2}>
          Calculate GST
        </h2>
        <p className={para}>
          Pick Add GST for a price without GST, or Remove GST for a total that already includes it.
        </p>
        <GstCalculator />
      </section>

      <section aria-labelledby="formulas" className="grid gap-3">
        <h2 id="formulas" className={h2}>
          How to calculate GST: the formulas
        </h2>
        <p className={para}>
          Three sums cover most GST questions. Multiply by 1.1 to add GST, divide by 11 to find the
          GST in a total, and divide by 1.1 to get the price before GST.
        </p>
        <SimpleTable
          caption="GST formulas, with the A$100 and A$110 example"
          columns={["To", "Do this", "Example"]}
          rows={[
            {
              header: "Add GST to a price",
              cells: [
                "Multiply by 1.1",
                `${formatAud(example.net)} becomes ${formatAud(example.gross)}`,
              ],
            },
            {
              header: "Find the GST in a total",
              cells: [
                "Divide by 11",
                `${formatAud(example.gross)} contains ${formatAud(example.gst)}`,
              ],
            },
            {
              header: "Remove GST from a total",
              cells: [
                "Divide by 1.1",
                `${formatAud(example.gross)} becomes ${formatAud(example.net)}`,
              ],
            },
          ]}
        />
        <SourceLine
          sources={[{ label: "ATO, how GST works", href: ATO_HOW_GST_WORKS }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="add-gst" className="grid gap-3">
        <h2 id="add-gst" className={h2}>
          Add GST to a price
        </h2>
        <p className={para}>
          Multiply the price before GST by 1.1. A quote of {formatAud(addExample.net)} plus GST
          comes to {formatAud(addExample.gross)}: {formatAud(addExample.net)} for the work and{" "}
          {formatAud(addExample.gst)} GST. To see the GST on its own, multiply by 0.1 instead.
        </p>
      </section>

      <section aria-labelledby="remove-gst" className="grid gap-3">
        <h2 id="remove-gst" className={h2}>
          Remove GST from a price (reverse GST)
        </h2>
        <p className={para}>
          Divide the GST-inclusive total by 11 to find the GST, then subtract it. A receipt for{" "}
          {formatAud(removeExample.gross)} holds {formatAud(removeExample.gst)} of GST, so the price
          without GST is {formatAud(removeExample.net)}.
        </p>
        <p className={para}>
          Why 11 and not 10? GST is 10% of the price before GST. Add it and the total is 11 parts,
          one of which is GST. Taking 10% off a total gives the wrong answer: 10% of A$110 is A$11,
          but the GST in it is A$10.
        </p>
      </section>

      <section aria-labelledby="inclusive-exclusive" className="grid gap-3">
        <h2 id="inclusive-exclusive" className={h2}>
          GST-inclusive and GST-exclusive prices
        </h2>
        <SimpleTable
          caption="The same sale written two ways"
          columns={["Price is", "Also written", "Means", "Example"]}
          rows={[
            {
              header: "GST-inclusive",
              cells: [
                "incl. GST",
                "GST is already in the price",
                `${formatAud(example.gross)}, of which ${formatAud(example.gst)} is GST`,
              ],
            },
            {
              header: "GST-exclusive",
              cells: [
                "ex GST, excl. GST, plus GST",
                "GST is still to be added",
                `${formatAud(example.net)}, plus ${formatAud(example.gst)} GST`,
              ],
            },
          ]}
        />
        <p className={para}>
          Use Remove GST in the calculator for a GST-inclusive amount and Add GST for a
          GST-exclusive one. The ATO says businesses registered for GST include it in their prices.
        </p>
        <SourceLine
          sources={[{ label: "ATO, how GST works", href: ATO_HOW_GST_WORKS }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="several-items" className="grid gap-3">
        <h2 id="several-items" className={h2}>
          How GST is rounded
        </h2>
        <p className={para}>
          For a single sale, round the GST to the nearest cent; half a cent rounds up. For an
          invoice with several items, the ATO describes two methods: work out GST on the invoice
          total and round once, or work out GST on each item, add those up and round the total. The
          two can give slightly different totals, and you and your customer do not have to use the
          same one.
        </p>
        <p className={para}>
          This calculator uses the per-item method. Tick &ldquo;Several items&rdquo; to enter up to
          20 lines: GST on each line is rounded to the cent, then the lines are added up. Our
          invoice generator rounds once on the invoice total instead, so a long invoice there can
          differ from this tool by a cent or two.
        </p>
        <SimpleTable
          caption="Three items with GST included, rounded on each line"
          columns={["Item", "Excl. GST", "GST", "Incl. GST"]}
          rows={[
            ...EXAMPLE_ITEMS.map(([name], i) => {
              const l = itemsExample.lines[i]!
              return {
                header: name,
                cells: [formatAud(l.net), formatAud(l.gst), formatAud(l.gross)],
              }
            }),
            {
              header: "Total",
              cells: [
                formatAud(itemsExample.totals.net),
                formatAud(itemsExample.totals.gst),
                formatAud(itemsExample.totals.gross),
              ],
            },
          ]}
        />
        <SourceLine
          sources={[{ label: "ATO, tax invoices (rounding)", href: ATO_TAX_INVOICES }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="what-is-gst" className="grid gap-3">
        <h2 id="what-is-gst" className={h2}>
          What GST is
        </h2>
        <p className={para}>
          GST (goods and services tax) is a broad-based tax of 10% on most goods, services and other
          items sold or consumed in Australia. Businesses registered for GST include it in their
          prices and claim credits for the GST in what they buy for the business. Sales that are
          GST-free or input-taxed have no GST to add, so this calculator does not apply to them.
        </p>
        <SourceLine
          sources={[
            { label: "ATO, how GST works", href: ATO_HOW_GST_WORKS },
            { label: "ATO, GST", href: ATO_GST },
          ]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="threshold" className="grid gap-3">
        <h2 id="threshold" className={h2}>
          The A$75,000 registration threshold
        </h2>
        <p className={para}>
          You must register for GST when your GST turnover reaches A$75,000 or more. GST turnover is
          your gross income minus GST. You also must register if:
        </p>
        <ul className={`${para} ${list}`}>
          <li>you are a new business and expect to reach A$75,000 in your first year</li>
          <li>you run a non-profit organisation with GST turnover of A$150,000 or more</li>
          <li>you provide taxi, limousine or ride-sourcing travel, whatever your turnover</li>
          <li>you want to claim fuel tax credits.</li>
        </ul>
        <p className={para}>
          Otherwise registering is your choice; if you do register, you generally stay registered
          for 12 months. Register within 21 days of going over the threshold. You need an ABN first,
          and once registered you report GST on your BAS.
        </p>
        <SourceLine
          sources={[{ label: "ATO, registering for GST", href: ATO_REGISTER }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="tax-invoices" className="grid gap-3">
        <h2 id="tax-invoices" className={h2}>
          When you must give a tax invoice
        </h2>
        <p className={para}>
          If you are registered for GST and a customer asks for a tax invoice, you must give them
          one within 28 days. You do not have to for a sale of A$82.50 or less, including GST. A PDF
          or other digital invoice is fine. For a sale under A$1,000 the invoice shows:
        </p>
        <ul className={`${para} ${list}`}>
          <li>that the document is intended to be a tax invoice</li>
          <li>your identity and your ABN</li>
          <li>the date it was issued</li>
          <li>a brief description of what you sold, including quantity and price</li>
          <li>
            the GST amount, shown on its own line or, when GST is exactly one eleventh of the total,
            a statement such as &ldquo;Total price includes GST&rdquo;
          </li>
          <li>how much of each sale is taxable.</li>
        </ul>
        <p className={para}>
          For a sale of A$1,000 or more, the invoice also shows the buyer&rsquo;s identity or ABN.
          Our{" "}
          <a href="/guides/tax-invoice-requirements" className={linkClass}>
            tax invoice requirements guide
          </a>{" "}
          goes through each item, and the{" "}
          <a href="/invoice-generator" className={linkClass}>
            free invoice generator
          </a>{" "}
          makes a PDF tax invoice with the GST worked out.
        </p>
        <SourceLine
          sources={[{ label: "ATO, tax invoices", href: ATO_TAX_INVOICES }]}
          financialYear={fy}
          checked={checked}
        />
      </section>
    </>
  ),
  faq: [
    {
      question: "How do I calculate GST on a price?",
      answer:
        "Multiply the price before GST by 0.1 to get the GST, or by 1.1 to get the price including GST. On A$100, the GST is A$10 and the total is A$110.",
    },
    {
      question: "How do I remove GST from a total?",
      answer:
        "Divide the GST-inclusive total by 11 to find the GST, then subtract it. A$110 contains A$10 of GST, so the price before GST is A$100. Dividing the total by 1.1 gives the same A$100.",
    },
    {
      question: "Why divide by 11 and not 10?",
      answer:
        "GST is 10% of the price before GST, so a GST-inclusive total is 11 parts and GST is one of them. Taking 10% off A$110 gives A$11, but the GST in it is A$10.",
    },
    {
      question: "Is GST the same in NSW, Queensland and WA?",
      answer: `Yes. The ATO describes GST as a broad-based 10% tax on most goods and services sold or consumed in Australia, so the same 10% applies in every state and territory. Source: ATO, how GST works, checked ${checked}.`,
    },
    {
      question: "What is the difference between GST-inclusive and GST-exclusive?",
      answer:
        "A GST-inclusive price already contains the GST: A$110 including GST is A$100 plus A$10 GST. A GST-exclusive price (ex GST) has no GST yet: A$100 ex GST becomes A$110 once you add it.",
    },
    {
      question: "Do I need to register for GST?",
      answer: `You must register once your GST turnover (gross income minus GST) reaches A$75,000, or A$150,000 for a non-profit. Taxi, limousine and ride-sourcing drivers must register whatever their turnover. Below the threshold it is generally your choice. Source: ATO, registering for GST, checked ${checked}.`,
    },
    {
      question: "When must I give a tax invoice?",
      answer: `If you are registered for GST and a customer asks for one, you must give a tax invoice within 28 days. You do not have to for a sale of A$82.50 or less including GST. Source: ATO, tax invoices, checked ${checked}.`,
    },
    {
      question: "How is GST rounded?",
      answer: `To the nearest cent, with half a cent rounding up. For several items, the ATO allows rounding once on the invoice total or working out GST per item and adding it up. This calculator rounds each line to the cent, then adds the lines. Source: ATO, tax invoices, checked ${checked}.`,
    },
    {
      question: "Is this the ATO's GST calculator?",
      answer: `No. Kestly built this GST calculator independently; it is not an ATO tool. It applies the 10% rate and the rounding rules described on the ATO pages linked here, last checked ${checked}.`,
    },
  ],
}
